import { Label, Menu, Skeleton, SpotlightTarget } from '@neo4j-ndl/react';
import { BuildingOffice2IconOutline, CheckIconOutline, FolderIconOutline } from '@neo4j-ndl/react/icons';
import { Breadcrumbs } from '@neo4j-ndl/react/next';
import type { ProjectSummary } from '@nx/state';
import { PLAN_TYPE, PROJECT_TYPE, consoleApi, useActiveOrgSummary, useActiveProjectSummary } from '@nx/state';
import { isNonEmptyString, isNotNullish } from '@nx/stdlib';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo, useRef, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate, useParams } from 'react-router';

import { getMarketplaceProjectFriendlyName, isMarketplaceProject } from '../instance/entities/helpers';

const VdcLabel = () => {
  return (
    <Label className="h-4 items-center px-[6px]" fill="semi-filled">
      VDC
    </Label>
  );
};

export const OrgProjectNav = () => {
  const [isOrganizationMenuOpen, setIsOrganizationMenuOpen] = useState(false);
  const [isProjectMenuOpen, setIsProjectMenuOpen] = useState(false);
  const organizationMenuRef = useRef<HTMLButtonElement>(null);
  const projectMenuRef = useRef<HTMLButtonElement>(null);

  /** react router stuff */
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const isOrgPage = matchPath('/org/:orgId/*', location.pathname) !== null;

  /** fetch orgs and projects */
  const { data: userDetails } = consoleApi.useGetUserDetailsQuery();
  const { data: organizations = [] } = consoleApi.useListOrganizationsByUserQuery(userDetails?.id ?? skipToken);
  const listUserProjectsRes = consoleApi.useListProjectsByUserQuery(userDetails?.id ?? skipToken);

  const isRemovePlanTypeRestriction = userDetails?.featureToggles['console-remove-plantype'] ?? false;

  /** filter orgs and projects based on plan type */
  let userOrgs = organizations;
  let userProjects = useMemo(() => listUserProjectsRes.data ?? [], [listUserProjectsRes.data]);
  const isEnterpriseUser = userOrgs.some((org) => org.planType === PLAN_TYPE.ENTERPRISE);
  if (isEnterpriseUser && !isRemovePlanTypeRestriction) {
    userOrgs = userOrgs.filter((org) => org.planType === PLAN_TYPE.ENTERPRISE);
    userProjects = userProjects.filter((project) => project.planType === PLAN_TYPE.ENTERPRISE);
  }

  /** active org and project */
  const activeOrg = useActiveOrgSummary();
  const activeProject = useActiveProjectSummary();

  /** org menu items */
  const orgMenuItems = useMemo(() => {
    return userOrgs.map((org) => {
      const isCurrentOrg = org.id === activeOrg?.id;

      const title = (
        <div className="flex items-center justify-between">
          {org.displayName}
          {org.planType === PLAN_TYPE.ENTERPRISE && isRemovePlanTypeRestriction && <VdcLabel />}
        </div>
      );

      return (
        <Menu.Item
          key={org.id}
          title={title}
          as="button"
          onClick={() => {
            const orgProject = listUserProjectsRes.data?.find((project) => project.organizationId === org.id);
            if (isNotNullish(orgProject)) {
              localStorage.setItem(`default-project-${userDetails?.id}`, orgProject.id);
            }

            void navigate(`org/${org.id}`);
          }}
          icon={isCurrentOrg ? <CheckIconOutline /> : null}
        />
      );
    });
  }, [activeOrg?.id, isRemovePlanTypeRestriction, listUserProjectsRes.data, navigate, userDetails?.id, userOrgs]);

  /** project menu items */
  const projectMenuItems = useMemo(() => {
    const projects = userProjects.filter(
      (project) => isNonEmptyString(activeOrg?.id) && project.organizationId === activeOrg.id,
    );

    const gcpProjects = projects.filter((project) => project.projectType === PROJECT_TYPE.N4GCP);
    const awsProjects = projects.filter((project) => project.projectType === PROJECT_TYPE.MARKETPLACE_AWS);
    const azureProjects = projects.filter((project) => project.projectType === PROJECT_TYPE.MARKETPLACE_AZURE);
    const projectsInOrder: (string | ProjectSummary)[] = projects.filter(
      (project) => ![...gcpProjects, ...awsProjects, ...azureProjects].includes(project),
    );

    if (gcpProjects.length > 0) {
      projectsInOrder.push(...['divider', ...gcpProjects]);
    }
    if (awsProjects.length > 0) {
      projectsInOrder.push(...['divider', ...awsProjects]);
    }
    if (azureProjects.length > 0) {
      projectsInOrder.push(...['divider', ...azureProjects]);
    }

    return projectsInOrder.map((project, idx) => {
      if (typeof project === 'string') {
        return <Menu.Divider key={`${project}-${idx}`} />;
      }

      const projectRoute =
        isNonEmptyString(projectId) && location.pathname.includes(projectId)
          ? `${location.pathname.replace(projectId, project.id)}${location.search}`
          : `/projects/${project.id}`;

      const isCurrentProject = activeProject?.id === project.id;

      const description = isMarketplaceProject(project.projectType)
        ? `${getMarketplaceProjectFriendlyName(project.projectType)} Marketplace`
        : null;

      return (
        <Menu.Item
          key={project.id}
          title={project.name}
          description={description}
          as="button"
          onClick={() => {
            localStorage.setItem(`default-project-${userDetails?.id}`, project.id);
            void navigate(projectRoute);
          }}
          icon={isCurrentProject ? <CheckIconOutline /> : null}
        />
      );
    });
  }, [
    activeOrg?.id,
    activeProject?.id,
    location.pathname,
    location.search,
    navigate,
    projectId,
    userDetails?.id,
    userProjects,
  ]);

  /**
   * TODO: remove the crazy overflow-hidden and min-w-0 classes
   * once we have a native solution in the breadcrumbs component.
   * https://linear.app/neo4j/issue/DES-1246/next-breadcrumbs-shrink-support
   */
  return (
    <Breadcrumbs className="min-w-0 overflow-hidden [&_.ndl-breadcrumbs-list]:min-w-0 [&_.ndl-breadcrumbs-list]:!flex-nowrap [&_.ndl-breadcrumbs-list]:overflow-hidden">
      <SpotlightTarget id="welcome-tour-1-organization" indicatorVariant="point" className="overflow-hidden">
        {/** TODO: add possibility to add initial divider in needle, or move logo into the breadcrumbs */}
        <Breadcrumbs.Item className="before:text-palette-neutral-border-strong min-w-0 overflow-hidden before:font-sans before:text-sm before:font-bold before:content-['/'] [&_.ndl-breadcrumbs-item-inner]:min-w-0">
          {activeOrg ? (
            <>
              <Breadcrumbs.Button as={Link} htmlAttributes={{ to: `/org/${activeOrg.id}` }}>
                <BuildingOffice2IconOutline />
                <div className="block truncate">{activeOrg.displayName}</div>
                {activeOrg.planType === PLAN_TYPE.ENTERPRISE && isRemovePlanTypeRestriction && <VdcLabel />}
              </Breadcrumbs.Button>
              <Breadcrumbs.SelectButton
                ref={organizationMenuRef}
                ariaLabel="Open organization menu"
                onClick={() => {
                  setIsOrganizationMenuOpen(!isOrganizationMenuOpen);
                }}
              >
                <Menu
                  anchorRef={organizationMenuRef}
                  isOpen={isOrganizationMenuOpen}
                  onClose={() => setIsOrganizationMenuOpen(false)}
                >
                  <Menu.Items>
                    {orgMenuItems.length > 0 && (
                      <>
                        {orgMenuItems}
                        <Menu.Divider />
                      </>
                    )}
                    <Menu.Item title="All Organizations" as={Link} htmlAttributes={{ to: `/orgs` }} />
                    {/* <Menu.Divider />
              <Menu.Item title="Create Organization" icon={<PlusIconOutline />} /> */}
                  </Menu.Items>
                </Menu>
              </Breadcrumbs.SelectButton>
            </>
          ) : (
            <Skeleton className="h-8 w-36" onBackground="weak" />
          )}
        </Breadcrumbs.Item>
      </SpotlightTarget>

      {!isOrgPage && (
        <Breadcrumbs.Item className="overflow-hidden [&_.ndl-breadcrumbs-item-inner]:min-w-0">
          {activeProject ? (
            <>
              <Breadcrumbs.Button as={Link} htmlAttributes={{ to: `/projects/${activeProject.id}` }}>
                <FolderIconOutline />
                <div className="block truncate">{activeProject.name}</div>
              </Breadcrumbs.Button>
              {/** TODO: if we add the CREATE PROJECT button, we should always render the SelectButton */}
              {projectMenuItems.length > 1 && (
                <Breadcrumbs.SelectButton
                  ref={projectMenuRef}
                  ariaLabel="Open project menu"
                  onClick={() => {
                    setIsProjectMenuOpen(!isProjectMenuOpen);
                  }}
                >
                  <Menu
                    anchorRef={projectMenuRef}
                    isOpen={isProjectMenuOpen}
                    onClose={() => setIsProjectMenuOpen(false)}
                  >
                    <Menu.Items>
                      {projectMenuItems}
                      {/* <Menu.Divider />
              <Menu.Item title="Create Project" icon={<PlusIconOutline />} /> */}
                    </Menu.Items>
                  </Menu>
                </Breadcrumbs.SelectButton>
              )}
            </>
          ) : (
            <Skeleton className="h-8 w-36" onBackground="weak" />
          )}
        </Breadcrumbs.Item>
      )}
    </Breadcrumbs>
  );
};
